import { useTaskStore } from '../stores/taskStore';
import { useHabitStore } from '../stores/habitStore';
import { useEventStore } from '../stores/eventStore';
import { useProjectStore } from '../stores/projectStore';
import { useQuestStore } from '../stores/questStore';
import { useAbilitiesStore } from '../stores/abilitiesStore';
import { useProgramStore } from '../stores/programStore';
import { useRoutineStore } from '../stores/routineStore';
import { programStanding } from './programProgress';
import { CATEGORIES } from '../constants/categories';
import { ACTION_TYPE_BY_ID, capacityFor, type ActionTypeId } from '../constants/achievementBadges';
import type {
  Task, Habit, Event, Project, Quest, Ability, Program, ProgramInitiation, Routine,
} from '../types';
import { isSkillDone } from './skillProgress';

/**
 * The capacity calculation, as pure functions over a store snapshot.
 *
 * ⭐⭐ WHY THIS IS NOT A HOOK. Two callers need the same answer from different
 * places: the `CapacityIndicator` needs it REACTIVELY (re-render when a store
 * changes), and the create guard needs it IMPERATIVELY, at the instant of a
 * click. A hook can only serve the first. Computing it twice is how the two
 * would drift until the bar said 4/5 while the guard blocked — so the maths
 * lives here once, and both sides pass it a snapshot.
 *
 * ⚠️⚠️ THE GUARD MUST READ `getState()`, NOT A RENDER CLOSURE. A memoised
 * reader captures the stores as they were when the component last rendered;
 * two creates dispatched inside one React batch would both see the pre-first
 * state and the second would sail past a cap the first had just filled. This is
 * the same stale-closure shape that erased a weekday toggle in the inbox.
 */

// ─────────────────────────────────────────────────────────────────────────────

/** Everything the capacity maths reads. Assembled once, passed everywhere. */
export interface CapacitySnapshot {
  tasksByDomain: Record<string, Task[]>;
  habits: Habit[];
  events: Event[];
  projects: Project[];
  quests: Quest[];
  abilities: Ability[];
  programs: Program[];
  initiations: ProgramInitiation[];
  routines: Routine[];
}

/** A live snapshot straight off the stores — for imperative callers (the guard). */
export function readCapacitySnapshot(): CapacitySnapshot {
  const programStore = useProgramStore.getState();
  return {
    tasksByDomain: useTaskStore.getState().tasks,
    habits: useHabitStore.getState().habits,
    events: useEventStore.getState().events,
    projects: useProjectStore.getState().projects,
    quests: useQuestStore.getState().quests,
    abilities: useAbilitiesStore.getState().abilities,
    programs: programStore.programs,
    initiations: programStore.initiations,
    routines: useRoutineStore.getState().routines,
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * One active holding, named well enough to pick out of a list.
 *
 * ⭐ The block modal offers to complete or archive these. It must be handed the
 * SAME records the counter counted — if the list and the count come from two
 * predicates, the user completes something, the number does not move, and the
 * escape hatch is a lie. Hence: the count IS `items.length`.
 */
export interface ActiveItem {
  id: string;
  label: string;
  /** Quest id for a goal, ability id for a skill — the per-parent types. */
  parentId?: string;
}

export type ActiveByDomain = Record<string, Record<ActionTypeId, ActiveItem[]>>;

const named = <T extends { id: string }>(list: T[], label: (x: T) => string, parentId?: string): ActiveItem[] =>
  list.map(x => ({ id: x.id, label: label(x) || 'Untitled', ...(parentId ? { parentId } : {}) }));

/**
 * ⭐ WHAT "ACTIVE" MEANS, PER TYPE — the mirror of `completionCounts`, which
 * counts finished work. Anything completed or archived stops occupying the
 * allowance, because a cap on things you already did would be a lifetime quota
 * rather than a work-in-progress limit.
 *
 * ⚠️⚠️ ARCHIVED RECORDS STAY IN THEIR STORE ARRAYS. `habitStore.habits` keeps
 * archived habits (flagged `archivedAt`), and Programs and Routines carry an
 * `archived` boolean rather than leaving the list. Counting them as active is
 * not merely an off-by-a-few: archiving is the ONLY escape those three types
 * have, so a count that ignores the flag makes the block modal's own remedy do
 * nothing. All three filters below are load-bearing — see `capacityCore.test.ts`.
 *
 * ⚠️ Abilities and Skills have no completed, archived or done state at all, so
 * every one of them counts while it exists. That is deliberate, and it is why
 * their block modal offers the Inbox and nothing else.
 */
export function activeItemsByDomain(snap: CapacitySnapshot): ActiveByDomain {
  const out: ActiveByDomain = {};

  for (const { id } of CATEGORIES) {
    const dQuests = snap.quests.filter(
      q => q.category === id && q.status !== 'completed' && q.status !== 'archived');
    const dAbilities = snap.abilities.filter(a => a.category === id);

    out[id] = {
      tasks: named((snap.tasksByDomain[id] || []).filter(t => !t.completed), t => t.title),
      // ⚠️ `!h.archivedAt` — archived habits remain in `habits`.
      habits: named(snap.habits.filter(h => h.category === id && !h.archivedAt), h => h.name),
      projects: named(
        snap.projects.filter(p => p.category === id && p.status !== 'completed' && p.status !== 'archived'),
        p => p.title),
      events: named(snap.events.filter(e => e.category === id && !e.completed), e => e.title),
      quests: named(dQuests, q => q.title),
      // ⚠️ A DOMAIN TOTAL FOR GOALS IS NOT THE ALLOWANCE — goals are capped PER
      // QUEST. This total exists so the type has a domain-level reading at all;
      // callers pass a `parentId` and get the quest's own list instead. Same
      // for skills.
      goals: dQuests.flatMap(q =>
        named((q.goals || []).filter(g => !g.completed), g => `${g.actionVerb} ${g.what}`.trim(), q.id)),
      abilities: named(dAbilities, a => a.name),
      skills: dAbilities.flatMap(a =>
        named((a.skills || []).filter(s => !isSkillDone(s)), s => s.name, a.id)),
      // ⭐ Reuses `programStanding` so "finished" means the same thing it does on
      // the Progress page — completion lives on the RUN, not the Program.
      // ⚠️ `!p.archived` — an archived program stays in `programs`.
      programs: named(
        snap.programs.filter(
          p => p.category === id && !p.archived && !programStanding(p.id, snap.initiations)?.completed),
        p => p.title),
      // ⚠️ `!r.archived` — an archived routine stays in `routines`.
      routines: named(snap.routines.filter(r => r.category === id && !r.archived), r => r.name),
    };
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────

export type DomainCounts = Record<ActionTypeId, number>;

/**
 * What each domain has actually DONE, per action type — the input every badge
 * is derived from, and the number that RAISES an allowance.
 *
 * ⭐ Counted in ONE place for all ten types and all seven domains. The user
 * badge is the minimum across domains, so the per-domain and per-user surfaces
 * have to agree exactly; two counting passes would eventually disagree and the
 * user badge would contradict the domain badges it is built from.
 *
 * ⚠️ Every count is of FINISHED work. The two exceptions are deliberate and are
 * not "completions":
 *  · **Abilities** LEVEL rather than complete, so their levels are the count.
 *  · **Habits** and **Routines** repeat, so their repetitions are the count —
 *    a habit is never "done".
 */
export function completionCounts(snap: CapacitySnapshot): Record<string, DomainCounts> {
  const out: Record<string, DomainCounts> = {};

  for (const { id } of CATEGORIES) {
    const dQuests = snap.quests.filter(q => q.category === id);
    const dAbilities = snap.abilities.filter(a => a.category === id);

    out[id] = {
      tasks: (snap.tasksByDomain[id] || []).filter(t => t.completed).length,
      habits: snap.habits
        .filter(h => h.category === id)
        .reduce((sum, h) => sum + (h.completedDates?.length || 0), 0),
      projects: snap.projects.filter(p => p.category === id && p.status === 'completed').length,
      events: snap.events.filter(e => e.category === id && e.completed).length,
      quests: dQuests.filter(q => q.status === 'completed').length,
      // ⚠️ Goals are counted across the domain's quests, INCLUDING quests still
      // running — a finished goal is finished whether or not the quest around
      // it is.
      goals: dQuests.reduce((sum, q) => sum + (q.goals?.filter(g => g.completed).length || 0), 0),
      abilities: dAbilities.reduce((sum, a) => sum + (a.level || 0), 0),
      // A skill is mastered when every requirement is done — the one rule the
      // ability tree and its prerequisite locks use (utils/skillProgress).
      skills: dAbilities.reduce(
        (sum, a) => sum + (a.skills?.filter(s => isSkillDone(s)).length || 0), 0),
      // ⭐ Reuses `programStanding`, so "finished" means here exactly what it
      // means on the Progress page — completion lives on the RUN, and a program
      // you completed then restarted still counts.
      programs: snap.programs.filter(
        p => p.category === id && programStanding(p.id, snap.initiations)?.completed).length,
      routines: snap.routines
        .filter(r => r.category === id)
        .reduce((sum, r) => sum + (r.completedCount || 0), 0),
    };
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * How full a domain is for one action type.
 *
 * ⚠️⚠️ TWO DIFFERENT NUMBERS, AND SWAPPING THEM IS SILENT. `max` is derived from
 * the LIFETIME COMPLETION count (what raises the badge rank, via `capacityFor`);
 * `used` is the ACTIVE holding right now. They are counted from different
 * predicates on purpose — completions only ever grow, holdings go up and down.
 * Feeding the completion count to both would draw a bar that always looks full
 * and never moves when you finish something.
 */
export interface CapacityStatus {
  /** Active holdings — what fills the allowance. Always `items.length`. */
  used: number;
  /** The allowance the domain's badge rank has unlocked. */
  max: number;
  /**
   * `'quest'` / `'ability'` when the allowance is PER PARENT rather than per
   * domain. The label must print it or "5 of 10" reads as a domain total.
   */
  per?: string;
  /**
   * ⚠️ Over is not a fault. Ranks come from completions, so an established
   * domain can legitimately hold more than a fresh one's allowance — those
   * accounts keep everything they have and are only stopped from adding more.
   */
  over: boolean;
  /**
   * ⚠️ THERE WAS A `percent` HERE AND IT IS DELETED, NOT DEPRECATED. It was
   * `min(100, used/max)`, which is the same number for 4-of-3 and 9-of-3 — so
   * the bar it fed sat pinned at 100% on any established domain and never moved.
   * Anything reintroducing it has to answer what a bar shows past full first.
   */
  /**
   * The very records counted in `used` — what the block modal offers to clear.
   * Same array the count came from, so clearing one always moves the number.
   */
  items: ActiveItem[];
}

/**
 * ⚠️ Returns `null` when the answer would be a guess — an unknown domain, or a
 * per-parent type asked without a parent. Both consumers treat `null` as
 * "no opinion": the indicator renders nothing, and **the guard ALLOWS**. A cap
 * that fires when it could not compute is how you lock someone out of their own
 * app; see the header of `capacityGuard.ts`.
 */
export function capacityStatus(
  snap: CapacitySnapshot,
  active: ActiveByDomain,
  completions: Record<string, DomainCounts>,
  type: ActionTypeId,
  domainId: string,
  parentId?: string,
): CapacityStatus | null {
  const def = ACTION_TYPE_BY_ID[type];
  const domainCompletions = completions[domainId];
  const domainActive = active[domainId];
  if (!def || !domainCompletions || !domainActive) return null;

  // ⚠️ The RANK is always the domain's, even when the allowance is per parent:
  // you earn "3 goals per quest" by finishing goals anywhere in the domain, not
  // inside one quest.
  const max = capacityFor(def, domainCompletions[type]);

  let items: ActiveItem[];
  if (def.capacityPer) {
    if (!parentId) return null;
    // ⚠️ The parent must EXIST. A stale id would otherwise filter to an empty
    // list and report 0 used — a wide-open cap on a quest that isn't there.
    const parentExists = type === 'goals'
      ? snap.quests.some(q => q.id === parentId)
      : snap.abilities.some(a => a.id === parentId);
    if (!parentExists) return null;
    items = domainActive[type].filter(i => i.parentId === parentId);
  } else {
    items = domainActive[type];
  }

  const used = items.length;
  return {
    used,
    max,
    per: def.capacityPer,
    over: used > max,
    items,
  };
}
